import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, FormArray} from '@angular/forms';
import {VehicleService} from '../../../../../service/vehicle.service';
import {AccountService} from '../../../../../service/account.service';
import {MakeModel} from '../../../../../model/make.model';
import {DashboardAccountInterface} from '../../../../../model/dashboard-account.interface';
import {MessageService} from 'primeng/api';
import {DialogService} from 'primeng/dynamicdialog';
import {BulkUpdateService} from '../../../../../service/bulk-update.service';
import {BulkUpdateFilterPreviewComponent} from './bulk-update-filter-preview/bulk-update-filter-preview.component';
import {Subscription} from 'rxjs';
import {EventEmitterService} from '../../../../../service/event-emitter.service';

@Component({
    selector: 'app-bulk-update-account-filter',
    templateUrl: './bulk-update-account-filter.component.html',
    styleUrls: ['./bulk-update-account-filter.component.scss'],
    providers: [DialogService],
    standalone: false
})
export class BulkUpdateAccountFilterComponent implements OnInit, OnDestroy {
  filterForm: FormGroup;
  formChangesSubscription: Subscription;
  hasFormChanges = false;

  regionOptions = [
    {label: 'Northern', value: 'Northern'},
    {label: 'Western', value: 'Western'},
    {label: 'Eastern', value: 'Eastern'},
    {label: 'Southern', value: 'Southern'},
    {label: 'Central', value: 'Central'}
  ];

  propertyOptions = [
    {label: 'Make', value: 'Account Make'},
    {label: 'MPOP Account (Name or ID)', value: 'Account'},
    {label: 'OEM Program', value: 'OEM Program'},
    {label: 'Region', value: 'Region Name'}
  ];

  makeOptions: { label: string, value: number }[] = [];
  accounts: DashboardAccountInterface[] = [];
  appliedFilters: {accountNames: [], accountIds: []};
  showPreview = false;
  accountNameOptions: { label: string; value: number }[] = [];
  oemProgramOptions: { label: string; value: string }[]= [];
  excludedAccountIds: number[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private vehicleService: VehicleService,
    private accountService: AccountService,
    private messageService: MessageService,
    private bulkUpdateService: BulkUpdateService,
    private dialogService: DialogService,
    private eventEmitterService: EventEmitterService
  ) {
  }

  ngOnInit(): void {
    this.initForm();
    this.loadMakes();
    this.loadAccounts();
    this.loadOemPrograms();
    this.subscribeToFormChanges();
  }

  /**
   * Load OEM programs from the API
   */
  private loadOemPrograms(): void {
    this.bulkUpdateService.getOemPrograms().subscribe({
      next: (oemPrograms) => {
        this.oemProgramOptions = oemPrograms.map((program) => ({
          label: this.getOemProgramName(program.name),
          value: program.name
        }));
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load OEM programs.'
        });
      }
    });
  }

  /**
   * Subscribe to form changes to track when the form is modified
   */
  private subscribeToFormChanges(): void {
    this.formChangesSubscription = this.filterForm.valueChanges.subscribe(() => {
      this.hasFormChanges = true;
    });
  }

  /**
   * Clean up subscriptions when component is destroyed
   */
  ngOnDestroy(): void {
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
  }

  /**
   * Check if any filter has a non-empty value
   * @returns True if at least one filter has a non-empty value, false otherwise
   */
  hasValidFilters(): boolean {
    let result = true;
    for (const row of this.filterRows.controls) {
      const values = row.get('values').value;
      if ((typeof values === 'string' && values.trim() === '') ||
        (typeof values === 'object' && values.length === 0)) {
        result = false;
        break;
      }
    }

    return result;
  }

  /**
   * Handle beforeunload event to show a confirmation dialog when the user tries to leave the page with unsaved changes
   * @param event The beforeunload event
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasFormChanges) {
      event.preventDefault();
    }
  }

  /**
   * Initialize the filter form
   */
  private initForm(): void {
    this.filterForm = this.formBuilder.group({
      filterRows: this.formBuilder.array([])
    });
  }

  /**
   * Get the filter rows form array
   */
  get filterRows(): FormArray {
    return this.filterForm.get('filterRows') as FormArray;
  }

  /**
   * Add a new filter row
   */
  addFilterRow(): void {
    const filterRow = this.formBuilder.group({
      property: ['Account'],
      values: [[]]
    });

    this.filterRows.push(filterRow);
  }

  /**
   * Remove a filter row
   * @param index The index of the row to remove
   */
  removeFilterRow(index: number): void {
    this.filterRows.removeAt(index);
  }

  /**
   * Load makes from the Vehicle service
   */
  private loadMakes(): void {
    this.vehicleService.getAllMakes().subscribe(
      (makes: MakeModel[]) => {
        this.makeOptions = makes.map((make) => ({
          label: make.displayName,
          value: make.id
        }));
      }
    );
  }

  /**
   * Load accounts from the API
   */
  private loadAccounts(): void {
    this.accountService.getDashboardAccounts(true).subscribe({
      next: (accounts: DashboardAccountInterface[]) => {
        const eligibleAccounts = accounts.filter((account) => account.mediaAdCopyBulkUpdateEnabled);
        this.accounts = eligibleAccounts.sort((first, second) => first.name.localeCompare(second.name));
        this.accountNameOptions = eligibleAccounts.map((account) => ({
          label: account.name,
          value: account.accountId
        }));
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load MPOP accounts.'
        });
      }
    });
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    while (this.filterRows.length > 0) {
      this.filterRows.removeAt(0);
    }

    this.showPreview = false;
    this.hasFormChanges = false;
  }

  /**
   * Preview accounts based on applied filters
   */
  previewAccounts(): void {
    this.getAccountPreview();
  }

  /**
   * Open the preview modal with the filtered accounts
   */
  private openPreviewModal(): void {
    const ref = this.dialogService.open(BulkUpdateFilterPreviewComponent, {
      header: 'Preview Accounts',
      width: '50vw',
      modal: true,
      closable: true,
      data: {
        appliedFilters: this.appliedFilters,
        excludedAccountIds: this.excludedAccountIds
      }
    });

    ref.onClose.subscribe((result: any) => {
      if (result && Array.isArray(result.excludedAccountIds)) {
        this.excludedAccountIds = result.excludedAccountIds;
      }
    });
  }

  /**
   * Handle property change in a filter row
   * @param index The index of the row
   * @param newProperty The new property value
   */
  onPropertyChange(index: number, newProperty: string): void {
    const row = this.filterRows.at(index);
    row.patchValue({values: []});
  }

  /**
   * Apply filters to accounts
   */
  getAccountPreview(): void {
    let filterRequest = this.buildPreviewFilterRequest();
    let request = {
      accountId: 0,
      filters: filterRequest
    };
    this.bulkUpdateService.getAccountPreviewFromFilters(request).subscribe({
      next: (appliedFilters) => {
        this.appliedFilters = appliedFilters;
        // Emit the applied filters so other components (e.g., media-ad-copy-bulk-update) can consume them
        this.eventEmitterService.emitEvent({ name: 'mediaAdCopyFiltersChanged', payload: this.appliedFilters });
        this.hasFormChanges = false;
        this.openPreviewModal();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load account preview.'
        });
      }
    });
  }

  /**
   * Builds the preview filter request
   */
  private buildPreviewFilterRequest() {
    let filterRequest = [];
    for (const row of this.filterRows.controls.values()) {
      let value = row.value.values;
      let property = row.value.property;
      if (Array.isArray(value) && value.length > 0) {
        value = value.map(item => item.value).join(',');
      }
      if (property === 'Account') {
        property = 'Account ID';
      }

      filterRequest.push(
        {
          filterName: property,
          filterType: 'account',
          operator: 'in',
          value: value,
        }
      );
    }

    return filterRequest;
  }

  /**
   * Gets the disabled tooltip
   */
  getDisabledToolTip() {
    let result = null;
    if (!this.hasValidFilters()) {
      result = 'Fields must be filled out before previewing the filters';
    }

    return result;
  }

  private getOemProgramName(programName: string) {
    if (programName === 'Ford') {
      programName = 'Ford Direct';
    }
    return programName;
  }
}
